import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getCoreRowModel } from '@tanstack/react-table';

import { Add } from '@mui/icons-material';
import { Box, Button, TextField, Typography } from '@mui/material';

import { makeScopeTemplateColumn } from '../../../utils/columns/makeScopeTemplateColumn';
import { addScopeTemplate, deleteScopeTemplate, getScopeTemplates } from '../../../services/scopetemplate.service';

import { Table, useStandardTable as useTable } from '../../common/table';
import CustomModal from '../../common/withModal';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

const ScopeTemplates = () => {
  const navigate = useNavigate();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string>();
  const [paramFilter, setParamFilter] = useState<any>({ sortBy: [{ name: '1' }] });
  const forceReload = () => setParamFilter({ ...paramFilter, changedAt: new Date() });

  const onEdit = (value) => {
    value && navigate(`/scopetemplates/${value.id}`);
  };

  const onDelete = (value: string) => {
    setDeleteTargetId(value);
    setIsDeleteDialogOpen(true);
  };

  const initiateDeleteScopeTemplate = async () => {
    if (deleteTargetId) {
      await deleteScopeTemplate(deleteTargetId).then(() => {
        forceReload();
      });
    }
    setIsDeleteDialogOpen(false);
  };

  const columns = useMemo(() => makeScopeTemplateColumn(onEdit, onDelete), []);
  const table = useTable<any>({
    columns,
    accessKey: 'scopetemplates',
    fetchAsync: getScopeTemplates,
    params: paramFilter,
    serverSide: true,
  });

  const handleSubmit = async (values) => {
    await addScopeTemplate(values.name).then((x: any) => {
      if (x.status === 201) {
        navigate(`/scopetemplates/${x.data.id}`);
      }
    });
  };

  const validationSchema = () => {
    return Yup.object<any>({
      name: Yup.string().required('Name is required'),
    });
  };

  const formik = useFormik({
    initialValues: { name: '' },
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  return (
    <>
      <Box sx={{ px: { xs: 0.5, sm: 0.75 }, mb: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            rowGap: 1,
            columnGap: 1.5,
            mb: 1,
          }}
        >
          <Typography variant='h5' sx={{ m: 0, fontWeight: 700, color: '#1f2d63' }}>
            Scope Templates
          </Typography>
          <Button variant='contained' size='small' startIcon={<Add />} onClick={() => setIsAddDialogOpen(true)} sx={tablePrimaryButtonSx}>
            Add Scope Template
          </Button>
        </Box>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          Utilizing a scope template enables the efficient application of predefined scopes to individual users. Through this mechanism, users can
          inherit all pertinent scopes from the template, facilitating seamless implementation of scope modifications across multiple users with ease.
        </Typography>
      </Box>

      <CustomModal
        maxWidth='lg'
        title='Add Scope Template'
        isOpen={isAddDialogOpen}
        isValid={formik.isValid}
        setIsOpen={setIsAddDialogOpen}
        onConfirm={formik.handleSubmit}
      >
        <TextField
          id='name'
          name='name'
          label='Name'
          value={formik.values?.name ?? ''}
          onChange={formik.handleChange}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          placeholder='Name'
          size='small'
          fullWidth
          required
          margin='dense'
        />
      </CustomModal>
      <CustomModal
        maxWidth='lg'
        title='Delete Scope Template'
        isOpen={isDeleteDialogOpen}
        isValid={true}
        setIsOpen={setIsDeleteDialogOpen}
        onConfirm={initiateDeleteScopeTemplate}
      >
        <span className='txt-italic'>
          This action affects all(!) users who inherit their scopes from this template. Are you sure you want to continue?
        </span>
      </CustomModal>
      <Table {...table} showSearchBar={true} showColumnToggler={false} showAction={true} getCoreRowModel={getCoreRowModel()} />
    </>
  );
};

export default ScopeTemplates;
